import React, { useState } from 'react';
import { Edit2, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { StudyNote, VerseReference } from '../types';
import { BoxDiagram } from './BoxDiagram';
import { HighlightableText } from './HighlightableText';

interface NoteCardProps {
  note: StudyNote;
  onEdit: (note: StudyNote) => void;
  onDelete: (id: string) => void;
  onToggleVerse: (noteId: string, verseId: string) => void;
  onAddReference: (noteId: string, parentId: string) => void;
  onEditReference: (noteId: string, reference: VerseReference) => void;
  onDeleteReference: (noteId: string, parentId: string | null, referenceId: string) => void;
}

export const NoteCard: React.FC<NoteCardProps> = ({ 
  note, 
  onEdit, 
  onDelete, 
  onToggleVerse,
  onAddReference,
  onEditReference,
  onDeleteReference
}) => {
  const [isReferencesExpanded, setIsReferencesExpanded] = useState(true);

  return (
    <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow border border-gray-200">
      <div className="flex justify-between items-start mb-3">
        <h3 className="text-xl font-bold text-blue-700">
          {note.verse.display}
        </h3>
        <div className="flex gap-2">
          <button
            onClick={() => onEdit(note)}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit note"
          >
            <Edit2 size={18} />
          </button>
          <button
            onClick={() => onDelete(note.id)}
            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete note"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      {note.verse.verseText && (
        <div className="mb-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm max-h-48 overflow-y-auto">
          <HighlightableText
            text={note.verse.verseText}
            highlights={note.verseHighlights || []}
            onChange={() => {}}
            editable={false}
            className="text-gray-800 italic whitespace-pre-wrap"
          />
        </div>
      )}

      {note.content && (
        <div className="mb-3 text-gray-700 text-sm whitespace-pre-wrap">
          <HighlightableText
            text={note.content}
            highlights={note.noteHighlights || []}
            onChange={() => {}}
            editable={false}
            className="whitespace-pre-wrap"
          />
        </div>
      )}

      {note.linkedVerses.length > 0 && (
        <div className="border-t border-gray-200 pt-3">
          <button
            onClick={() => setIsReferencesExpanded(!isReferencesExpanded)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2 hover:text-blue-600 transition-colors"
          >
            {isReferencesExpanded ? (
              <ChevronDown size={16} className="text-blue-600" />
            ) : (
              <ChevronRight size={16} className="text-blue-600" />
            )}
            <span>References ({note.linkedVerses.length})</span>
          </button>
          {isReferencesExpanded && (
            <BoxDiagram
              mainVerse={note.verse.display}
              mainContent={note.content ?? ''}
              references={note.linkedVerses}
              noteId={note.id}
              onToggleVerse={onToggleVerse}
              onAddReference={onAddReference}
              onEditReference={onEditReference}
              onDeleteReference={onDeleteReference}
            />
          )}
        </div>
      )}

      <div className="text-xs text-gray-400 mt-3">
        Updated: {new Date(note.updatedAt).toLocaleDateString()}
      </div>
    </div>
  );
};
